/**
 * Mission page addresses: /mission/firstname-lastname-8j24ij — readable (whose
 * page it is) and unique without a numbered queue (john-doe-1, john-doe-2…).
 * Pure, no Firebase: shared by signup, the dashboard, the migration script and
 * tests (tests/missionPath.test.ts).
 */

/** Convert an arbitrary string to a URL-safe path segment ("Jörg Müller" → "jorg-muller"). */
export function toPathSegment(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .replace(/_/g, '-')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const SUFFIX = /^[a-z0-9]{6}$/;

/** Six stable [a-z0-9] characters for a user id — the same uid always gets the same suffix. */
export function pathSuffix(uid: string): string {
  // Two FNV-1a passes with different seeds → ~62 bits, plenty for 6 base-36 chars.
  let a = 0x811c9dc5, b = 0x01000193 ^ 0x5bd1e995;
  for (let i = 0; i < uid.length; i++) {
    const c = uid.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x5bd1e995) >>> 0;
  }
  return (a.toString(36) + b.toString(36)).padStart(6, '0').slice(-6);
}

/** A random suffix, for the (rare) case the stable one is already taken. */
export function randomSuffix(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return [...bytes].map((x) => '0123456789abcdefghijklmnopqrstuvwxyz'[x % 36]).join('');
}

/** "Jussi", "Savolainen", uid → "jussi-savolainen-8j24ij". No name → "missionary-8j24ij". */
export function missionPath(firstName: string | undefined, lastName: string | undefined, uid: string, suffix = pathSuffix(uid)): string {
  const name = toPathSegment(`${firstName || ''} ${lastName || ''}`) || 'missionary';
  return `${name}-${suffix}`;
}

/** True when a path already has this user's id suffix (so nothing to migrate). */
export function hasIdSuffix(path: string | undefined, uid: string): boolean {
  const tail = path?.split('-').pop() ?? '';
  return SUFFIX.test(tail) && tail === pathSuffix(uid);
}
